/**
 * Remembers a coarse traffic-source category in a first-party cookie so content
 * surfaces can adapt even after the UTM params leave the URL (e.g. after an
 * in-app redirect) and across return visits within the attribution window.
 *
 * This is a FUNCTIONAL signal, not analytics: it stores only a category
 * ("affiliate"), never full attribution, and is used to decide which content
 * variant to show. Affiliate traffic (utm_medium=affiliate) is shown the
 * explicit ("spicy") explore variant by default; unknown / organic traffic
 * keeps the safe default. See contentGateService.isExplicitContentAllowed().
 */

const TRAFFIC_SOURCE_COOKIE = "kf_traffic_source";
const TRAFFIC_SOURCE_DAYS = 30;
const AFFILIATE_SOURCE = "affiliate";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const cookies = document.cookie.split(";").map((cookie) => cookie.trim());
  const match = cookies.find((cookie) => cookie.startsWith(`${name}=`));
  if (!match) {
    return null;
  }
  return decodeURIComponent(match.split("=").slice(1).join("="));
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") {
    return;
  }
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function currentUrlMedium(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const medium = new URLSearchParams(window.location.search).get("utm_medium");
    return medium ? medium.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Persist the traffic-source category when the current URL identifies one.
 * Safe to call on every navigation; only writes for recognized sources.
 */
export function captureTrafficSourceFromUrl(): void {
  if (currentUrlMedium() === AFFILIATE_SOURCE) {
    writeCookie(TRAFFIC_SOURCE_COOKIE, AFFILIATE_SOURCE, TRAFFIC_SOURCE_DAYS);
  }
}

export function getTrafficSource(): string | null {
  return readCookie(TRAFFIC_SOURCE_COOKIE);
}

/**
 * True when the visitor arrived from an affiliate — either right now (UTM still
 * in the URL) or earlier in this attribution window (remembered cookie).
 */
export function isAffiliateSource(): boolean {
  if (currentUrlMedium() === AFFILIATE_SOURCE) {
    return true;
  }
  return getTrafficSource() === AFFILIATE_SOURCE;
}
